import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from '../../design-system';
import { TrashIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { SectionCard, SECTIONS } from '../IdeaOverviewEditor';
import { useUpdateIdea } from '../../hooks/api/useIdeas';
import { useDeleteTask } from '../../hooks/api/useTasks';
import { useApp } from '../../contexts/AppContext';

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  roofSoft:   '#F2D1B3',
  stem:       '#D4A373',
  r2: '6px', r3: '10px', r4: '14px', r5: '20px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ── Mushroom SVG ─────────────────────────────────────────────────
const Mushroom = ({ size = 44, color = T.capRed, rot = 0 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" style={{ transform: `rotate(${rot}deg)`, flexShrink: 0 }}>
    <path d="M6 22 C6 12, 16 5, 24 5 C32 5, 42 12, 42 22 C42 24, 40 25, 38 25 L10 25 C8 25, 6 24, 6 22 Z" fill={color}/>
    <ellipse cx="16" cy="14" rx="3" ry="2.5" fill="#FFFBEC" opacity="0.9"/>
    <ellipse cx="28" cy="11" rx="2.5" ry="2" fill="#FFFBEC" opacity="0.9"/>
    <ellipse cx="34" cy="18" rx="2" ry="1.8" fill="#FFFBEC" opacity="0.9"/>
    <path d="M16 25 L16 40 C16 42, 18 44, 20 44 L28 44 C30 44, 32 42, 32 40 L32 25 Z" fill={T.stem}/>
    <line x1="14" y1="26" x2="34" y2="26" stroke={T.ink} strokeWidth="0.8" opacity="0.3"/>
  </svg>
);

// ── Section wrapper ───────────────────────────────────────────────
const IdeaSection = ({ kicker, title, hand, children, action }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14, gap: 16 }}>
      <div>
        {kicker && (
          <div style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: T.inkMuted, textTransform: 'uppercase', marginBottom: 4 }}>
            {kicker}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.3px' }}>{title}</h2>
          {hand && (
            <span style={{ fontFamily: T.hand, fontSize: 20, color: T.orange, transform: 'translateY(-1px) rotate(-2deg)', display: 'inline-block' }}>
              {hand}
            </span>
          )}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ── Stat pill ─────────────────────────────────────────────────────
const StatPill = ({ label, value, hint, color }) => (
  <div style={{
    background: T.card,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r3,
    padding: '12px 16px',
    boxShadow: T.sh1,
    minWidth: 0,
  }}>
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.7, color: T.inkMuted, textTransform: 'uppercase', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || T.ink, lineHeight: 1, fontFamily: T.sans }}>
      {value}
    </div>
    {hint && <div style={{ fontSize: 11, color: T.inkMuted, marginTop: 4 }}>{hint}</div>}
  </div>
);

// ── Plan row ──────────────────────────────────────────────────────
export const PlanRow = ({ plan, isLast, onDeleted }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();
  const deleteMutation = useDeleteTask();
  const [hovering, setHovering] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const STATUS = {
    completed:  { dot: T.moss,    label: 'Ready' },
    processing: { dot: T.orange,  label: 'Generating' },
    submitted:  { dot: T.inkFaint, label: 'Not started' },
    error:      { dot: T.capRed,  label: 'Error' },
  };
  const s = STATUS[plan.status] || STATUS.submitted;

  const handleDeleteConfirm = async () => {
    setShowConfirm(false);
    try {
      await deleteMutation.mutateAsync(plan.id);
      showSuccess('Plan deleted');
      onDeleted?.();
    } catch {
      showError('Failed to delete plan');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: isLast ? 'none' : `1px solid ${T.rule}`,
        backgroundColor: hovering ? T.paperDeep : 'transparent',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <button
        onClick={() => navigate(`/plans/${plan.id}`)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
          padding: '11px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: T.sans,
          color: T.ink,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: '13px', color: T.ink, margin: 0 }}>
            {plan.name || 'Unnamed Plan'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: s.dot, display: 'inline-block' }} />
            <span style={{ fontSize: '11px', color: s.dot, fontWeight: 600 }}>{s.label}</span>
          </div>
          <ChevronRightIcon style={{ width: 14, height: 14, color: T.inkFaint }} />
        </div>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
        disabled={deleteMutation.isPending}
        title="Delete plan"
        style={{
          display: hovering ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          marginRight: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          borderRadius: T.r2,
          color: T.inkMuted,
          flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.capRed; e.currentTarget.style.backgroundColor = 'rgba(193,74,51,0.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <TrashIcon style={{ width: 14, height: 14 }} />
      </button>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Delete Plan" size="sm">
        <div style={{ marginBottom: '16px', color: T.ink, fontSize: 14 }}>
          Are you sure you want to delete <strong>{plan.name || 'this plan'}</strong>? This cannot be undone.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={() => setShowConfirm(false)}
            style={{ padding: '7px 14px', background: 'transparent', border: `1px solid ${T.rule}`, borderRadius: T.r2, cursor: 'pointer', fontFamily: T.sans, fontSize: 13, color: T.inkMuted }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            style={{ padding: '7px 14px', background: T.capRed, border: 'none', borderRadius: T.r2, cursor: 'pointer', fontFamily: T.sans, fontSize: 13, fontWeight: 700, color: '#fff' }}
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ── Plans rail ────────────────────────────────────────────────────
const PlansRail = ({ plans, onCreatePlan, onRefetch }) => {
  const [, setSearchParams] = useSearchParams();
  return (
  <div style={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: T.inkMuted, textTransform: 'uppercase' }}>
          Plans
        </div>
        <button
          onClick={onCreatePlan}
          style={{ background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer', fontFamily: T.sans }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
        >
          + New
        </button>
      </div>
      {plans.length === 0 ? (
        <div style={{ padding: '16px', border: `1px dashed ${T.rule}`, borderRadius: T.r3, textAlign: 'center', backgroundColor: T.paperSoft }}>
          <div style={{ fontSize: 12, color: T.inkFaint, fontStyle: 'italic' }}>
            No plans yet. Create one to start building.
          </div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${T.rule}`, borderRadius: T.r3, overflow: 'hidden', backgroundColor: T.card, boxShadow: T.sh1 }}>
          {plans.map((plan, idx) => (
            <PlanRow key={plan.id} plan={plan} isLast={idx === plans.length - 1} onDeleted={onRefetch} />
          ))}
        </div>
      )}
    </div>

    <div style={{ padding: 16, background: T.paperSoft, border: `1px dashed ${T.ruleStrong}`, borderRadius: T.r3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 15, color: T.orange }}>✦</span>
        <div style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>Edit with AI</div>
      </div>
      <div style={{ fontSize: 12, color: T.inkMuted, lineHeight: 1.5, marginBottom: 10 }}>
        Refine scope, priorities, and assumptions in plain English.
      </div>
      <button
        style={{ width: '100%', padding: '8px', background: T.orange, border: 'none', borderRadius: T.r3, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.sans }}
        onClick={() => setSearchParams(prev => { prev.set('tab', 'chat'); return prev; }, { replace: true })}
      >
        Open chat →
      </button>
    </div>
  </div>
  );
};

// ── Custom displays for collapsible sections ──────────────────────

const JourneysDisplay = ({ value }) => {
  const journeys = Array.isArray(value) ? value : [];
  if (!journeys.length) return <Empty>No user journeys defined yet.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {journeys.map((j, i) => (
        <div key={i} style={{ background: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r3, padding: '14px 16px', boxShadow: T.sh1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: j.steps?.length ? 10 : 0 }}>
            <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, color: T.orange, letterSpacing: 0.5 }}>#{i + 1}</span>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{j.name || `Journey ${i + 1}`}</div>
          </div>
          {j.steps?.length > 0 && (
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {j.steps.map((step, si) => (
                <li key={si} style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{step}</li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );
};

const ConstraintsDisplay = ({ value }) => {
  const items = Array.isArray(value) ? value : [];
  if (!items.length) return <Empty>No constraints defined yet.</Empty>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r3, boxShadow: T.sh1 }}>
          <span style={{ fontSize: 14, color: T.inkMuted, flexShrink: 0, marginTop: 1 }}>⊘</span>
          <span style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{item}</span>
        </div>
      ))}
    </div>
  );
};

const OutOfScopeDisplay = ({ value }) => {
  const items = Array.isArray(value) ? value : [];
  if (!items.length) return <Empty>Nothing explicitly out of scope yet.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: T.paperSoft, border: `1px dashed ${T.rule}`, borderRadius: T.r3 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: T.capRed, flexShrink: 0, marginTop: 1 }}>✕</span>
          <span style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{item}</span>
        </div>
      ))}
    </div>
  );
};

const RisksDisplay = ({ value }) => {
  const risks = Array.isArray(value) ? value : [];
  if (!risks.length) return <Empty>No risks identified yet.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {risks.map((r, i) => (
        <div key={i} style={{ background: T.card, border: `1px solid ${T.rule}`, borderLeft: `3px solid ${T.orange}`, borderRadius: T.r3, padding: '14px 16px', boxShadow: T.sh1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, marginBottom: 6 }}>
            {r.description || r.risk || `Risk ${i + 1}`}
          </div>
          {r.mitigation && (
            <div style={{ padding: '8px 10px', background: T.mossSoft, border: `1px dashed ${T.moss}`, borderRadius: T.r2 }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 800, letterSpacing: 0.6, color: T.mossDark, textTransform: 'uppercase', marginBottom: 3 }}>
                Mitigation
              </div>
              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{r.mitigation}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Empty = ({ children }) => (
  <div style={{ padding: '14px 16px', background: T.paperSoft, border: `1px dashed ${T.rule}`, borderRadius: T.r3, fontSize: 13, color: T.inkFaint, fontStyle: 'italic' }}>
    {children}
  </div>
);

const SECTION_DISPLAY = {
  user_journeys: JourneysDisplay,
  constraints:   ConstraintsDisplay,
  out_of_scope:  OutOfScopeDisplay,
  risks:         RisksDisplay,
};

// ── Collapsible section ───────────────────────────────────────────
const CollapsibleSection = ({ sectionKey, spec, ideaId, onRefetch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const cfg = SECTIONS[sectionKey];
  const val = spec[sectionKey];
  const isEmpty = !val || (Array.isArray(val) ? val.length === 0 : false);
  const Display = SECTION_DISPLAY[sectionKey];

  return (
    <div style={{ borderTop: `1px solid ${T.rule}` }}>
      {/* Toggle row */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => { setIsOpen(v => !v); setIsEditing(false); }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: T.sans,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: '13px', color: T.ink }}>{cfg.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isEmpty && <span style={{ fontSize: 12, color: T.inkFaint, fontStyle: 'italic' }}>Not set</span>}
            <ChevronDownIcon style={{ width: 16, height: 16, color: T.inkFaint, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
          </div>
        </button>
        {isOpen && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            style={{ marginLeft: 12, background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer', fontFamily: T.sans, flexShrink: 0 }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
          >
            Edit
          </button>
        )}
      </div>

      {/* Content */}
      {isOpen && (
        <div style={{ paddingBottom: 16 }}>
          {isEditing ? (
            <SectionCard
              section={sectionKey}
              config={cfg}
              value={val}
              ideaId={ideaId}
              onUpdate={() => { setIsEditing(false); onRefetch(); }}
              onCancel={() => setIsEditing(false)}
              hideHeader
              initialEditing
              containerStyle={{ border: 'none', background: 'none', padding: 0, borderRadius: 0, marginBottom: 0 }}
            />
          ) : (
            Display ? <Display value={val} /> : <Empty>No content yet.</Empty>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────
const OverviewContent = ({ idea, plans, gapCount: gapCountProp, onRefetch, onCreatePlan, isMobile }) => {
  const { showSuccess, showError } = useApp();
  const [showOriginal, setShowOriginal] = useState(false);
  const [editingOriginal, setEditingOriginal] = useState(false);
  const [originalText, setOriginalText] = useState(idea.idea || '');
  const updateIdeaMutation = useUpdateIdea();

  const spec = idea.idea_spec || {};

  const handleSaveOriginal = async () => {
    try {
      await updateIdeaMutation.mutateAsync({ ideaId: idea.id, updates: { idea: originalText } });
      showSuccess('Idea updated');
      setEditingOriginal(false);
      onRefetch();
    } catch (err) {
      showError(err.message || 'Failed to update idea');
    }
  };

  const gapCount = gapCountProp ?? 0;

  // Compute drift mismatches from drift_state on the detail object
  const driftCount = (() => {
    const ds = idea.drift_state;
    if (!ds || typeof ds !== 'object') return 0;
    const features = ds.spec_features;
    if (!Array.isArray(features)) return 0;
    return features.filter(f => f.status === 'not_implemented' || f.status === 'partially_implemented').length;
  })();

  // Count stories across all plans (more useful than spec.features which doesn't exist)
  const storyCount = plans.reduce((acc, p) => acc + (p.story_count ?? p.stories?.length ?? 0), 0);

  const personas = Array.isArray(spec.target_users) ? spec.target_users : [];
  const metrics  = Array.isArray(spec.success_metrics) ? spec.success_metrics : [];

  const plansRail = (
    <PlansRail plans={plans} onCreatePlan={onCreatePlan} onRefetch={onRefetch} />
  );

  return (
    <div style={{ display: isMobile ? 'block' : 'flex', gap: '32px', alignItems: 'flex-start' }}>

      {/* LEFT: spec content */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {isMobile && <div style={{ marginBottom: 24 }}>{plansRail}</div>}

        {/* Hero */}
        <div style={{ padding: '6px 0 24px', borderBottom: `1px dashed ${T.ruleStrong}`, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <Mushroom size={48} rot={-10} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.hand, fontSize: 22, color: T.orange, lineHeight: 1 }}>your idea —</div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: T.ink, margin: '4px 0 6px', letterSpacing: '-0.6px', lineHeight: 1.1 }}>
                {idea.name}
              </h1>
              {idea.idea && (
                <p style={{ fontSize: 14, color: T.ink2, margin: '0 0 10px', maxWidth: 660, lineHeight: 1.6 }}>
                  {idea.idea.length > 200 ? idea.idea.slice(0, 200) + '…' : idea.idea}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {idea.current_phase && (
                  <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, padding: '3px 8px', borderRadius: T.full, background: T.mossSoft, color: T.mossDark, textTransform: 'uppercase', border: `1px solid ${T.moss}` }}>
                    {idea.current_phase.replace(/_/g, ' ')}
                  </span>
                )}
                <button
                  onClick={() => setShowOriginal(v => !v)}
                  style={{ background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, fontSize: 11, fontWeight: 700, padding: '3px 8px', cursor: 'pointer', fontFamily: T.sans }}
                >
                  {showOriginal ? 'Hide original idea' : 'Show original idea'}
                </button>
              </div>
            </div>
          </div>

          {/* Original idea (collapsible) */}
          {showOriginal && (
            <div style={{ marginTop: 16, padding: 16, background: T.paperSoft, border: `1px dashed ${T.rule}`, borderRadius: T.r3 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, letterSpacing: 0.7, color: T.inkMuted, textTransform: 'uppercase' }}>Original idea</span>
                {!editingOriginal && (
                  <button
                    onClick={() => { setOriginalText(idea.idea || ''); setEditingOriginal(true); }}
                    style={{ background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, fontSize: 11, padding: '3px 8px', cursor: 'pointer', fontFamily: T.sans }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
                  >
                    Edit
                  </button>
                )}
              </div>
              {editingOriginal ? (
                <div>
                  <textarea
                    value={originalText}
                    onChange={(e) => setOriginalText(e.target.value)}
                    rows={6}
                    style={{ width: '100%', padding: '10px 12px', backgroundColor: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r3, color: T.ink, fontSize: '13px', fontFamily: T.sans, resize: 'vertical', outline: 'none', lineHeight: 1.6, boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setEditingOriginal(false); setOriginalText(idea.idea || ''); }} style={{ background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, cursor: 'pointer', fontSize: '13px', padding: '6px 12px', fontFamily: T.sans }}>Cancel</button>
                    <button onClick={handleSaveOriginal} disabled={updateIdeaMutation.isPending} style={{ background: T.orange, border: 'none', borderRadius: T.r3, color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, padding: '6px 14px', fontFamily: T.sans, opacity: updateIdeaMutation.isPending ? 0.7 : 1 }}>
                      {updateIdeaMutation.isPending ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: '14px', lineHeight: 1.65, color: T.inkMuted, margin: 0, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                  {idea.idea || 'No original idea text.'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Executive summary */}
        {spec.executive_summary && (
          <IdeaSection kicker="Executive summary" title="What it is" hand="in plain English">
            <SectionCard
              section="executive_summary"
              config={SECTIONS.executive_summary}
              value={spec.executive_summary}
              ideaId={idea.id}
              onUpdate={onRefetch}
              containerStyle={{ border: 'none', background: 'none', padding: 0, borderRadius: 0, marginBottom: 0 }}
              renderDisplay={(value) => (
                <div style={{ padding: 20, background: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r3, boxShadow: T.sh1 }}>
                  <p style={{ fontSize: '15px', lineHeight: 1.75, color: T.ink2, margin: 0, whiteSpace: 'pre-wrap' }}>{value}</p>
                </div>
              )}
            />
          </IdeaSection>
        )}

        {/* Problem statement */}
        {spec.problem_statement && (
          <IdeaSection kicker="Problem" title="Why it matters">
            <SectionCard
              section="problem_statement"
              config={SECTIONS.problem_statement}
              value={spec.problem_statement}
              ideaId={idea.id}
              onUpdate={onRefetch}
              containerStyle={{ border: 'none', background: 'none', padding: 0, borderRadius: 0, marginBottom: 0 }}
              renderDisplay={(value) => (
                <div style={{ padding: 20, background: T.paperSoft, border: `1px solid ${T.rule}`, borderRadius: T.r3, borderLeft: `3px solid ${T.orange}` }}>
                  <p style={{ fontSize: '14px', lineHeight: 1.7, color: T.ink2, margin: 0, whiteSpace: 'pre-wrap' }}>{value}</p>
                </div>
              )}
            />
          </IdeaSection>
        )}

        {/* Target users */}
        {personas.length > 0 && (
          <IdeaSection kicker="People" title="Who it's for">
            <SectionCard
              section="target_users"
              config={SECTIONS.target_users}
              value={spec.target_users}
              ideaId={idea.id}
              onUpdate={onRefetch}
              containerStyle={{ border: 'none', background: 'none', padding: 0, borderRadius: 0, marginBottom: 0 }}
              renderDisplay={() => (
                <div style={{ display: 'grid', gridTemplateColumns: personas.length === 1 ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {personas.map((p, i) => {
                    const name = p.persona || p.name || `User ${i + 1}`;
                    const initials = name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
                    return (
                      <div key={i} style={{ padding: 16, background: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r3, boxShadow: T.sh1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: T.full, background: T.mossSoft, border: `1px solid ${T.moss}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: T.mossDark, flexShrink: 0 }}>
                            {initials}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{name}</div>
                        </div>
                        {p.description && (
                          <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55, margin: 0 }}>{p.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            />
          </IdeaSection>
        )}

        {/* Success metrics */}
        {metrics.length > 0 && (
          <IdeaSection kicker="What we'll measure" title="Success metrics">
            <SectionCard
              section="success_metrics"
              config={SECTIONS.success_metrics}
              value={spec.success_metrics}
              ideaId={idea.id}
              onUpdate={onRefetch}
              containerStyle={{ border: 'none', background: 'none', padding: 0, borderRadius: 0, marginBottom: 0 }}
              renderDisplay={() => (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(metrics.length, 3)}, 1fr)`, gap: 10 }}>
                  {metrics.map((m, i) => {
                    // Try to extract a leading number/value for visual punch
                    const match = typeof m === 'string' && m.match(/^([<>≤≥~]?\s*[\d.,]+\s*[%xX×kmKMBb+]?\s*)/);
                    const value = match ? match[1].trim() : null;
                    const label = match ? m.slice(match[0].length).trim() : m;
                    return (
                      <div key={i} style={{ padding: 16, background: T.mossSoft, border: `1px dashed ${T.moss}`, borderRadius: T.r3 }}>
                        {value && (
                          <div style={{ fontSize: 22, fontWeight: 800, color: T.mossDark, lineHeight: 1, marginBottom: 6, fontFamily: T.sans }}>
                            {value}
                          </div>
                        )}
                        <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{label || m}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            />
          </IdeaSection>
        )}

        {/* Collapsible sections */}
        <div style={{ marginTop: 8 }}>
          {['user_journeys', 'constraints', 'out_of_scope', 'risks'].map(key => (
            <CollapsibleSection key={key} sectionKey={key} spec={spec} ideaId={idea.id} onRefetch={onRefetch} />
          ))}
          <div style={{ borderTop: `1px solid ${T.rule}` }} />
        </div>

      </div>

      {/* RIGHT: plans rail (desktop only) */}
      {!isMobile && (
        <div style={{ width: 260, flexShrink: 0 }}>
          {plansRail}
        </div>
      )}

    </div>
  );
};

export default OverviewContent;
